/**
 * Sequent adapter CDO editor — pin-to-message configuration.
 *
 * Defines WHAT each physical pin does: board address, pin number, polarity,
 * and the LN2 message it maps to. Does NOT define WHO (bank/lift) —
 * that routing is configured on the Banks page via BankPinAssignmentMatrix.
 *
 * Existing bank_id / lift_id values on entries are preserved when editing
 * pin/message fields here — they are simply not shown or editable.
 */
import React, { useMemo } from "react";
import { Collapse, Button, Tooltip, Select, InputNumber, Switch } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faFileCode } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import FormSelectDevType from "../../../../components/form/FormSelectDevType";
import DrawerRowDivider from "../../../../components/drawer/DrawerRowDivider";
import FlatbufferMessagePicker from "../../../../components/select/FlatbufferMessagePicker";
import {
  AdapterCdoSequentInterface,
  DiscreteToLn2EntryInterface,
} from "../../../../../interfaces/Adapter.interface";
import { RootState } from "../../../../../app/store";
import { LiftType } from "../../../../../flatbuffers/generated/archive/lift-type";
import { DevType } from "../../../../../flatbuffers/generated/front-end/dev-type";
import { FbMessageRef } from "../../../../../interfaces/FbMessage.interface";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEV_TYPE_OPTIONS = Object.entries(DevType)
  .filter(([, v]) => typeof v === "number")
  .map(([label, value]) => ({ label, value: value as number }));

function pinCountForDevType(devType: number): number {
  return devType === 1 ? 8 : 16;
}

function msgToRef(entry: DiscreteToLn2EntryInterface): FbMessageRef | null {
  const m = entry.value.message;
  if (m.encoded_type === 0 && m.message_content === 0 && m.message_value === 0) return null;
  return {
    encodedType: m.encoded_type,
    messageContent: m.message_content,
    messageValue: m.message_value,
    deviceType: m.fbs_device_type as LiftType,
    label: "",
  };
}

function applyRef(
  entry: DiscreteToLn2EntryInterface,
  ref: FbMessageRef | null,
): DiscreteToLn2EntryInterface {
  return {
    ...entry,
    value: {
      ...entry.value,
      message: {
        ...entry.value.message,
        encoded_type: ref?.encodedType ?? 0,
        message_content: ref?.messageContent ?? 0,
        message_value: ref?.messageValue ?? 0,
        fbs_device_type: (ref?.deviceType ?? LiftType.ELEVATOR) as LiftType,
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CdoSequentPage({
  setCdo,
  cdoValue,
}: {
  setCdo: (newValue: AdapterCdoSequentInterface) => void;
  cdoValue: AdapterCdoSequentInterface;
}) {
  const { theme } = useSelector((state: RootState) => state.home);
  const { Panel } = Collapse;
  const [t] = useTranslation("global");

  const maxPin = pinCountForDevType(cdoValue.dev_type);

  // Group entries by board_address
  const boardGroups = useMemo(() => {
    const groups = new Map<number, { entry: DiscreteToLn2EntryInterface; idx: number }[]>();
    cdoValue.discrete_2_message.forEach((e, idx) => {
      const addr = e.key.board_address ?? 0;
      if (!groups.has(addr)) groups.set(addr, []);
      groups.get(addr)!.push({ entry: e, idx });
    });
    const sorted = Array.from(groups.entries()).sort(([a], [b]) => a - b);
    sorted.forEach(([, pins]) => {
      pins.sort((a, b) => (a.entry.key.pin_num ?? 0) - (b.entry.key.pin_num ?? 0));
    });
    return sorted;
  }, [cdoValue.discrete_2_message]);

  // ---- Mutation helpers ----

  const onChangeDevType = (newValue: number) => {
    const copy = structuredClone(cdoValue);
    copy.dev_type = newValue;
    setCdo(copy);
  };

  const updateEntry = (
    index: number,
    updater: (e: DiscreteToLn2EntryInterface) => DiscreteToLn2EntryInterface,
  ) => {
    const copy = structuredClone(cdoValue);
    copy.discrete_2_message[index] = updater(structuredClone(copy.discrete_2_message[index]));
    setCdo(copy);
  };

  const addEntry = (boardAddress: number) => {
    const usedPins = new Set(
      cdoValue.discrete_2_message
        .filter((e) => e.key.board_address === boardAddress)
        .map((e) => e.key.pin_num),
    );
    let nextPin = 1;
    while (usedPins.has(nextPin) && nextPin <= maxPin) nextPin += 1;
    if (nextPin > maxPin) nextPin = 1;

    const newEntry: DiscreteToLn2EntryInterface = {
      key: { board_address: boardAddress, pin_num: nextPin },
      value: {
        message: {
          encoded_type: 0,
          message_content: 0,
          message_value: 0,
          fbs_device_type: LiftType.ELEVATOR,
          position: null,
        },
        dev_type: cdoValue.dev_type,
        active_high: true,
      },
    };
    const copy = structuredClone(cdoValue);
    copy.discrete_2_message.push(newEntry);
    setCdo(copy);
  };

  const removeEntry = (index: number) => {
    const copy = structuredClone(cdoValue);
    copy.discrete_2_message.splice(index, 1);
    setCdo(copy);
  };

  // ---- Render ----

  const renderPinRow = (
    entry: DiscreteToLn2EntryInterface,
    globalIdx: number,
    boardAddr: number,
  ) => {
    const key = entry.key;
    return (
      <div
        key={globalIdx}
        style={{
          border: "1px solid #303030",
          borderRadius: 6,
          padding: "8px 12px",
          marginBottom: 8,
          background: "#141414",
        }}
      >
        <div className="row" style={{ alignItems: "center" }}>
          {/* Pin # */}
          <div className="col-6 col-md-1 mt-1">
            <span className="generalStyles__inputLabel">Pin #</span>
            <InputNumber
              size="small"
              min={1}
              max={maxPin}
              value={key.pin_num ?? 1}
              style={{ marginTop: 4, width: "100%" }}
              onChange={(val) => {
                if (val === null) return;
                updateEntry(globalIdx, (en) => ({
                  ...en,
                  key: {
                    ...en.key,
                    board_address: en.key.board_address ?? boardAddr,
                    pin_num: val,
                  },
                }));
              }}
            />
          </div>

          {/* Polarity */}
          <div className="col-6 col-md-2 mt-1">
            <span className="generalStyles__inputLabel">Active High</span>
            <div style={{ marginTop: 6 }}>
              <Switch
                size="small"
                checked={entry.value.active_high ?? true}
                onChange={(checked) =>
                  updateEntry(globalIdx, (en) => ({
                    ...en,
                    value: { ...en.value, active_high: checked },
                  }))
                }
              />
              <span style={{ marginLeft: 8, fontSize: 11, color: "#888" }}>
                {entry.value.active_high ? "HIGH -> active" : "LOW -> active"}
              </span>
            </div>
          </div>

          {/* LN2 Message */}
          <div className="col-12 col-md-3 mt-1">
            <span className="generalStyles__inputLabel">LN2 Message</span>
            <div style={{ marginTop: 4 }}>
              <FlatbufferMessagePicker
                value={msgToRef(entry)}
                onChange={(ref) => updateEntry(globalIdx, (en) => applyRef(en, ref))}
                allowClear
              />
            </div>
          </div>

          {/* Message device type */}
          <div className="col-12 col-md-2 mt-1">
            <span className="generalStyles__inputLabel">Dev Type</span>
            <Select
              className="generalStyles__width100"
              size="small"
              value={entry.value.dev_type}
              onChange={(val: number) =>
                updateEntry(globalIdx, (en) => ({
                  ...en,
                  value: { ...en.value, dev_type: val },
                }))
              }
              style={{ marginTop: 4 }}
            >
              {DEV_TYPE_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* Position */}
          <div className="col-6 col-md-2 mt-1">
            <span className="generalStyles__inputLabel">{t("general.position")}</span>
            <InputNumber
              size="small"
              value={entry.value.message.position ?? undefined}
              placeholder="—"
              style={{ marginTop: 4, width: "100%" }}
              onChange={(val) =>
                updateEntry(globalIdx, (en) => ({
                  ...en,
                  value: {
                    ...en.value,
                    message: { ...en.value.message, position: val ?? null },
                  },
                }))
              }
            />
          </div>

          {/* Delete */}
          <div className="col-6 col-md-1 mt-1">
            <span className="generalStyles__inputLabel" style={{ opacity: 0 }}>
              Del
            </span>
            <div style={{ marginTop: 4 }}>
              <Tooltip title={t("general.delete")}>
                <Button
                  danger
                  size="small"
                  shape="circle"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  onClick={() => removeEntry(globalIdx)}
                />
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <DrawerRowDivider icon={faFileCode} title="SEQUENT" />
      <Collapse>
        <Panel header={t("general.sequentFormulary")} key="1">
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <FormSelectDevType inputValue={cdoValue.dev_type} inputOnChange={onChangeDevType} />
            </div>
          </div>
        </Panel>
      </Collapse>
      <br />

      {/* Pin-to-message grid grouped by board */}
      <DrawerRowDivider icon={faFileCode} title={t("general.sequentPinToMessageMapping")} />
      {boardGroups.length === 0 && (
        <div style={{ color: "#888", padding: "8px 16px" }}>
          No pin mappings configured. Use &quot;Add Board&quot; to get started.
          <br />
          <span style={{ fontSize: 11, color: "#666" }}>
            Define which LN2 messages each pin produces. Lift/car assignment is done on the Banks
            page.
          </span>
        </div>
      )}

      {boardGroups.map(([addr, pins]) => (
        <div key={`board-${addr}`} style={{ marginBottom: 16, padding: "0 16px" }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#aaa",
              marginBottom: 6,
              borderBottom: "1px solid #303030",
              paddingBottom: 4,
            }}
          >
            Board {addr} ({pins.length} pin{pins.length !== 1 ? "s" : ""} &middot; {maxPin} max)
          </div>
          {pins.map(({ entry, idx }) => renderPinRow(entry, idx, addr))}
          <Button
            type="dashed"
            size="small"
            onClick={() => addEntry(addr)}
            style={{ marginTop: 4 }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
            Add Pin to Board {addr}
          </Button>
        </div>
      ))}

      <div style={{ padding: "0 16px" }}>
        <Button
          type="dashed"
          size="small"
          onClick={() => {
            const usedBoards = new Set(boardGroups.map(([addr]) => addr));
            let nextBoard = 0;
            while (usedBoards.has(nextBoard) && nextBoard < 8) nextBoard += 1;
            addEntry(nextBoard);
          }}
          style={{ marginTop: 8 }}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
          Add Pin on New Board
        </Button>
      </div>
      <br />
    </div>
  );
}
